import { CheckCircle2, Building2 } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { useSeo, pageSeo, clinicSchema, physicianSchema, graph, crumbs, absImage } from "@/lib/seo";
import { ImageSlot } from "@/components/common/ImageSlot";
import { CallButton, WhatsAppButton } from "@/components/common/ContactButtons";
import { CtaBand } from "@/components/common/CtaBand";
import { ABOUT_YOUNG_SPINE, ABOUT_SIGNOFF, CLINIC_STORY, PHILOSOPHY, DIAGNOSIS_INTRO, DIAGNOSIS_POINTS } from "@/data/copy";
import { accent } from "@/lib/ui";
import { keyed } from "@/lib/keys";

const Block = ({ eyebrow, title, children, tint }) => (
  <section className={`section ${tint ? "bg-gradient-to-br from-brand-tint via-white to-brand-greenTint" : ""}`}>
    <div className="container-x grid gap-8 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">{title}</h2>
      </div>
      <div className="lg:col-span-8">{children}</div>
    </div>
  </section>
);

const P = ({ children }) => <p className="mb-4 text-base leading-relaxed text-slate-700 md:text-lg">{children}</p>;

const ClinicPhotos = () => {
  const { content } = useSite();
  const photos = content.clinic_photos?.length ? content.clinic_photos : [null, null, null];
  return (
    <ul className="grid gap-4 sm:grid-cols-3">
      {photos.map((p, i) => (
        <li key={p?.url || i}>
          <ImageSlot image={p} label="Clinic photo" icon={Building2} ratio="aspect-[4/3]" testid={`clinic-photo-${i}`} />
        </li>
      ))}
    </ul>
  );
};

const Credentials = ({ content }) => (
  <ul className="mt-6 space-y-2" data-testid="about-credentials">
    {[content.doctor_credentials, content.doctor_experience, content.doctor_registration].filter(Boolean).map((c) => (
      <li key={c} className="flex items-start gap-3 text-base text-slate-700">
        <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-brand-green" aria-hidden="true" /> {c}
      </li>
    ))}
  </ul>
);

const AboutHero = () => {
  const { content } = useSite();
  return (
    <section className="mesh-hero grain relative overflow-hidden">
      <span className="blob -right-20 top-0 h-72 w-72 bg-brand-sky/25" aria-hidden="true" />
      <span className="blob -bottom-20 left-1/4 h-64 w-64 bg-brand-lime/25" aria-hidden="true" />
      <div className="container-x relative grid items-center gap-12 py-14 md:py-20 lg:grid-cols-12 lg:gap-16">
        <div className="relative mx-auto w-full max-w-sm lg:col-span-5">
          <span className="absolute -inset-3 -rotate-3 rounded-[2.25rem] bg-gradient-to-br from-brand-sky/50 via-brand-blue/20 to-brand-lime/50" aria-hidden="true" />
          <ImageSlot image={content.doctor_portrait} label={`${content.doctor_name} – portrait`} testid="about-doctor-portrait" className="relative shadow-xl" />
        </div>
        <div className="animate-rise lg:col-span-7">
          <p className="eyebrow">About Dr. Singh</p>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight text-brand-ink sm:text-5xl lg:text-6xl">{content.doctor_name}</h1>
          <p className="mt-3 text-lg font-medium text-brand-blue md:text-xl">{content.doctor_designation}</p>
          <Credentials content={content} />
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <CallButton testid="about-call-btn" />
            <WhatsAppButton testid="about-whatsapp-btn" />
          </div>
        </div>
      </div>
    </section>
  );
};

const DiagnosisSteps = () => (
  <ol className="space-y-3">
    {DIAGNOSIS_POINTS.map((d, i) => (
      <li key={d} className="flex gap-4 rounded-2xl bg-white p-4 ring-1 ring-slate-200">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl text-sm font-bold shadow-md ${accent(i).icon}`}>{i + 1}</span>
        <span className="text-base text-slate-700">{d}</span>
      </li>
    ))}
  </ol>
);

export default function About() {
  const { settings, content } = useSite();
  useSeo({
    ...pageSeo("/about"),
    ...(content.doctor_portrait?.url ? { image: absImage(content.doctor_portrait.url) } : {}),
    jsonLd: graph(clinicSchema(settings), physicianSchema(settings, content), crumbs(["About Dr. Singh", "/about"])),
  });
  return (
    <>
      <AboutHero />
      <Block eyebrow="Biography" title="Experience and focus">
        {keyed(content.doctor_bio.split(/\n\s*\n/)).map(([k, p]) => <P key={k}>{p}</P>)}
      </Block>
      <Block eyebrow="Approach to diagnosis" title="Understanding the person, not just the scan" tint>
        <P>{DIAGNOSIS_INTRO}</P>
        <DiagnosisSteps />
      </Block>
      <Block eyebrow="Philosophy" title="Patient-centred, holistic and preventive">
        <P>{PHILOSOPHY}</P>
      </Block>
      <Block eyebrow="Our story" title="About Young Spine" tint>
        <P>{ABOUT_YOUNG_SPINE}</P>
        <P>{CLINIC_STORY}</P>
        <p className="font-heading text-lg font-semibold text-brand-blue">{ABOUT_SIGNOFF}</p>
      </Block>
      <section className="section">
        <div className="container-x">
          <p className="eyebrow">The clinic</p>
          <h2 className="mb-8 font-heading text-2xl font-bold tracking-tight text-brand-ink sm:text-3xl">Inside Young Spine Tricity</h2>
          <ClinicPhotos />
        </div>
      </section>
      <CtaBand />
    </>
  );
}
